// The API answers errors as RFC 9457 problem+json (type, title, status, detail). Screens show
// error.message, so every failure becomes an ApiError whose message is something a reader can act on.
// Better Auth's own routes answer { code, message } instead; those are mapped here too.

export const FILTER_MESSAGE = "Some words in this aren’t allowed here. Your text is kept so you can change it.";

export class ApiError extends Error {
  constructor(
    message: string,
    /** The problem type's last segment ("rate-limited"), a Better Auth code, or "network". */
    readonly type: string,
    readonly status: number,
    /** For a 422: the fields that failed (problem+json errors[].path), e.g. "images.1.id". */
    readonly paths: string[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const FRIENDLY: Record<string, string> = {
  unauthenticated: "Sign in to continue.",
  "fellowship-not-enabled": "Turn on Fellowship in Settings to do this.",
  "profile-incomplete": "Finish your profile first: your own handle, your name and the age question.",
  "email-not-confirmed": "Confirm your email address first: we’ll send you a code.",
  "account-suspended": "Your account is suspended for now, so you can’t do this.",
  "fresh-sign-in-required": "For your safety, sign in again, then try this within five minutes.",
  "not-found": "This isn’t available any more.",
  conflict: "That’s already taken. Try another.",
  "edit-window-closed": "Posts can be edited for 24 hours after posting.",
  "payload-too-large": "That photo is too large. Try a smaller one.",
  "unsupported-media-type": "That kind of file isn’t accepted. Use a JPEG, PNG or WebP photo.",
  "image-unavailable": "A photo had to be sent again. Try posting once more.",
  "content-not-allowed": FILTER_MESSAGE,
  "limit-reached": "You’ve reached the limit for this.",
  "rate-limited": "You’re doing that a lot. Try again in a minute.",
  "feature-closed": "Fellowship is closed for now. Try again later.",
  validation: "Something in that isn’t quite right. Check it and try again.",
  internal: "Something went wrong on our side. Try again.",
  "auth-error": "That didn’t work. Check what you entered and try again.",
  "method-not-allowed": "That isn’t something this page can do. Reload and try again.",
  "idempotency-key-reused": "That was already sent. Reload to see it.",
  // Better Auth (email codes)
  INVALID_OTP: "That code isn’t right. Check the email and try again.",
  OTP_EXPIRED: "That code has expired. Send a new one.",
  TOO_MANY_ATTEMPTS: "Too many tries with that code. Send a new one.",
  INVALID_EMAIL: "Enter your email address, like name@example.com.",
  "provider-not-configured": "That sign-in method isn’t set up here yet. Use an email code for now.",
  "entitlement-required": "This isn’t in your library yet.",
  "content-unavailable": "This isn’t available yet: its rights are still being cleared.",
};

/** Problems whose detail is written for readers, so it's shown as is. */
const DETAIL_IS_FRIENDLY = new Set(["rate-limited", "feature-closed", "fresh-sign-in-required", "limit-reached", "account-suspended", "conflict"]);

type ProblemLike = { type?: unknown; title?: unknown; status?: unknown; detail?: unknown; code?: unknown; message?: unknown; errors?: unknown };

/** An error from a response and its parsed body (problem+json, Better Auth's { code, message }, or nothing). */
export function toApiError(response: Response, body: unknown): ApiError {
  const p = (body && typeof body === "object" ? body : {}) as ProblemLike;
  const status = typeof p.status === "number" ? p.status : response.status;
  // problem+json (the API, and Better Auth's routes once they answer in it) or Better Auth's { code, message }.
  const fromType = typeof p.type === "string" && p.type !== "about:blank" ? p.type.split("/").pop()! : null;
  // Sign-in errors arrive as problem+json of type auth-error with Better Auth's code beside it (the API's W7),
  // or, from an older API, as Better Auth's own { code, message }: either way the code says what happened.
  const raw = (fromType === "auth-error" || !fromType) && typeof p.code === "string" ? p.code : (fromType ?? `http-${response.status}`);
  // Better Auth's codes as problem types ("invalid-otp") and as codes ("INVALID_OTP") read the same.
  const type = FRIENDLY[raw] ? raw : (FRIENDLY[raw.toUpperCase().replace(/-/g, "_")] ? raw.toUpperCase().replace(/-/g, "_") : raw);
  const detail = typeof p.detail === "string" ? p.detail : null;
  const paths = Array.isArray(p.errors) ? p.errors.map((e) => (e && typeof e === "object" && typeof (e as { path?: unknown }).path === "string" ? (e as { path: string }).path : "")).filter(Boolean) : [];
  if (paths.length && type === "image-unavailable") return new ApiError(FRIENDLY[type]!, type, status, paths);
  // A switch that's closed carries the message the moderator wrote; a rate limit says how long.
  if (detail && DETAIL_IS_FRIENDLY.has(type)) return new ApiError(detail, type, status);
  if (FRIENDLY[type]) return new ApiError(FRIENDLY[type], type, status);
  if (status === 429) return new ApiError(FRIENDLY["rate-limited"]!, type, status);
  if (status >= 500) return new ApiError(FRIENDLY.internal!, type, status);
  const message = typeof p.message === "string" ? p.message : typeof p.title === "string" ? p.title : "That didn’t work. Try again.";
  return new ApiError(message, type, status);
}

export const networkError = () => new ApiError("We can’t reach Fellowship right now. Check your connection and try again.", "network", 0);
